import type { LabTemplate } from "./types.ts";

/** Корень audit-логов команд Kali (поведенческое оценивание).
 *  Per-run каталог маунтится в контейнер на /var/log/caudit. */
export const AUDIT_ROOT = process.env.CYBERALEM_AUDIT_ROOT ?? "/mnt/data/cyberalem/audit";

/** Каталог audit-лога конкретного запуска (на хосте). */
export function auditDirFor(runId: string): string {
  return `${AUDIT_ROOT}/${runId}`;
}

/**
 * Параметризация шаблона: ${RUN_ID} заменяется на конкретный запуск,
 * чтобы одинаковые сценарии могли работать одновременно
 * (run-a8f3-network, run-b42c-network, ...).
 */
export function renderTemplate(template: LabTemplate, runId: string): LabTemplate {
  const render = (value: string): string => value.replaceAll("${RUN_ID}", runId);
  return {
    ...template,
    machines: template.machines.map((m) => ({
      ...m,
      name: m.name,
      image: render(m.image),
      command: m.command ? render(m.command) : undefined,
      env: Object.fromEntries(
        Object.entries(m.env ?? {}).map(([k, v]) => [k, render(v)]),
      ),
      volumes: m.volumes?.map((v) => ({
        host: render(v.host),
        container: v.container,
      })),
    })),
  };
}

export function networkName(runId: string) {
  return `cyberalem-${runId}-net`;
}

/** Per-run egress-сеть для интернета: у КАЖДОГО запуска своя NAT-сеть.
 *  Workstation разных run'ов друг другу НЕ видны (общая сеть была
 *  вектором атаки между студентами: ttyd/noVNC внутри чужого контейнера). */
export function egressNetworkName(runId: string) {
  return `cyberalem-egress-${runId}`;
}

export function machineName(runId: string, machine: string) {
  return `cyberalem-${runId}-${machine}`;
}

/** Виртуальная подсеть лаборатории: 172.16.<rand>.0/24, детерминирована от run_id. */
export function labSubnet(runId: string) {
  const octet =
    (parseInt(
      runId
        .split("")
        .reduce((a, c) => a + c.charCodeAt(0), 0)
        .toString(16) ?? "0",
      16) % 250) + 2;
  return `172.16.${octet}.0/24`;
}

export const LAB_FLAG = "flag{cyberalem_range}";

// В mock-режиме флаг статичен, чтобы серверная авто-проверка (exact match)
// могла оценить ответ. Изоляция запусков при этом остаётся по run_id.
export function flagFor(_runId?: string) {
  return LAB_FLAG;
}

/* ------------------- Workstation OS таңдау (Kali/Parrot/Ubuntu) ------------------- */
export type LabOs = "kali" | "parrot" | "ubuntu";

export const LAB_OS_OPTIONS: Record<
  LabOs,
  { image: string; command: string; label: string; desc: string }
> = {
  kali: {
    image: "cyberalem/kali-ttyd:4.4",
    command: "start-desktop.sh",
    label: "Kali Linux",
    desc: "Offensive: nmap, sqlmap, metasploit, WiFi-суит, GUI + терминал.",
  },
  parrot: {
    image: "cyberalem/parrot-ttyd:1.4",
    command: "start-desktop.sh",
    label: "Parrot OS",
    desc: "Offensive + privacy: XFCE desktop, терминал, GUI + ttyd.",
  },
  ubuntu: {
    image: "cyberalem/ubuntu-ttyd:1.4",
    command: "start-desktop.sh",
    label: "Ubuntu 24.04",
    desc: "Blue/Admin: ufw, fail2ban, auditd, aide, sysstat, GUI + терминал.",
  },
};

export function isLabOs(v: unknown): v is LabOs {
  return v === "kali" || v === "parrot" || v === "ubuntu";
}

/** Шаблондағы workstation-машинаның бейнесін/командасын таңдалған OS-ке ауыстыру. */
export function withWorkstationOs(t: LabTemplate, os: LabOs): LabTemplate {
  const spec = LAB_OS_OPTIONS[os] ?? LAB_OS_OPTIONS.kali;
  return {
    ...t,
    machines: t.machines.map((m) =>
      m.role === "workstation" ? { ...m, image: spec.image, command: spec.command } : m,
    ),
  };
}

/** Машина атауы бойынша жеке OS (мысалы, арена: kali-red → Red ойыншының OS, kali-blue → Blue-тікіңі). */
export function withWorkstationOsMap(
  t: LabTemplate,
  map: Record<string, LabOs> | undefined | null,
): LabTemplate {
  if (!map) return t;
  return {
    ...t,
    machines: t.machines.map((m) => {
      const os = map[m.name];
      if (m.role === "workstation" && isLabOs(os)) {
        const spec = LAB_OS_OPTIONS[os];
        return { ...m, image: spec.image, command: spec.command };
      }
      return m;
    }),
  };
}

/**
 * «Жеке Kali ортасы» — шынайы Kali Linux контейнер (ttyd web-терминал,
 * браузерде ашылады) + оқу нысана «Shop v1.0» (IDOR осалдығы).
 * gateway_port: 0 = orchestrator хост-портты автоматты бөледі.
 */
export const KALI_LAB_TEMPLATE: LabTemplate = {
  scenario_id: "personal-kali",
  version: "1.1.0",
  name: "Жеке Kali (шынайы) + Shop target + демо-роутер",
  resources: { cpu: 8, memory_mb: 16384, disk_gb: 30 },
  limits: { max_containers: 3, ttl_minutes: 240 },
  network: { internet: false, university_network: false },
  machines: [
    {
      name: "kali",
      image: "cyberalem/kali-ttyd:4.4",
      role: "workstation",
      // Xvfb + XFCE desktop (noVNC 6080) + ttyd терминал (7681)
      command: "start-desktop.sh",
      gateway_port: 0,
      gui_port: 0,
      // Поведенческое оценивание: лог команд → хост (audit volume)
      volumes: [
        { host: `${AUDIT_ROOT}/${"${RUN_ID}"}`, container: "/var/log/caudit" },
      ],
    },
    {
      name: "target",
      image: "cyberalem/shop-target:2.0",
      role: "target",
      command: "mkdir -p /run/sshd && /usr/sbin/sshd && python3 /opt/forensics_setup.py && python3 /opt/shop.py",
      http: { port: 8080, path: "/healthz" },
    },
    {
      // Осал үй роутері — RCE/brute-force жаттығуы (reconnaissance арқылы IP-сін тап)
      name: "router",
      image: "cyberalem/demo-router:1.0",
      role: "target",
      command: "python3 /opt/router.py",
    },
  ],
};

/**
 * Арена «Shop 1v1»: Red (Kali) + Blue (Kali) + ортақ Target.
 * Бір желідегі үш контейнер — Red PREP-ке дейін Target-қа қосылмайды
 * (orchestrator желіден ажыратады), LIVE-де қосылады.
 */
export const ARENA_1V1_TEMPLATE: LabTemplate = {
  scenario_id: "arena-1v1",
  version: "1.0.0",
  name: "Арена Shop 1v1 (Red + Blue + Target)",
  resources: { cpu: 8, memory_mb: 16384, disk_gb: 40 },
  limits: { max_containers: 3, ttl_minutes: 240 },
  network: { internet: false, university_network: false },
  machines: [
    {
      name: "kali-red",
      image: "cyberalem/kali-ttyd:4.4",
      role: "workstation",
      command: "start-desktop.sh",
      gateway_port: 0,
      gui_port: 0,
    },
    {
      name: "kali-blue",
      image: "cyberalem/kali-ttyd:4.4",
      role: "workstation",
      command: "start-desktop.sh",
      gateway_port: 0,
      gui_port: 0,
    },
    {
      name: "target",
      image: "cyberalem/shop-target:2.0",
      role: "target",
      command: "mkdir -p /run/sshd && /usr/sbin/sshd && python3 /opt/forensics_setup.py && python3 /opt/shop.py",
      http: { port: 8080, path: "/healthz" },
    },
  ],
};
